import { api, fetchServer } from "@/features/utils/fetch-server";
import type { DossierFile, DossierFileSource } from "../types";

const base = (clientId: string) => `/api/dossiers/v1/${clientId}`;

/** A 20MB upload on a slow line outlives `fetchServer`'s default 30s. */
const UPLOAD_TIMEOUT_MS = 120_000;

export class DossiersAPI {
  static listFiles = async (
    clientId: string,
    customerId: string,
  ): Promise<{ files: DossierFile[] }> =>
    api.get<{ files: DossierFile[] }>(
      `${base(clientId)}/customers/${customerId}/files`,
    );

  /** `fetchServer` rather than `api.post` — this is multipart, not JSON. */
  static uploadFile = async (
    clientId: string,
    customerId: string,
    file: File,
    source: DossierFileSource,
  ): Promise<{ file: DossierFile }> => {
    const body = new FormData();
    body.append("file", file);
    body.append("origin", source.origin);
    if (source.originId) body.append("origin_id", source.originId);

    const response = await fetchServer(
      `${base(clientId)}/customers/${customerId}/files`,
      { method: "POST", body, timeout: UPLOAD_TIMEOUT_MS },
    );
    return response.json();
  };

  /**
   * The route is authenticated, so the file cannot be linked to: it comes back as
   * a blob, through `fetchServer`, for `openBlobInTab` / `downloadBlob`.
   */
  static downloadFile = async (
    clientId: string,
    fileId: string,
  ): Promise<Blob> => {
    const response = await fetchServer(
      `${base(clientId)}/files/${fileId}/download`,
      { method: "GET" },
    );
    return response.blob();
  };

  static renameFile = async (
    clientId: string,
    fileId: string,
    filename: string,
  ): Promise<{ file: DossierFile }> =>
    api.patch<{ file: DossierFile }, { filename: string }>(
      `${base(clientId)}/files/${fileId}`,
      {
        filename,
      },
    );

  static removeFile = async (
    clientId: string,
    fileId: string,
  ): Promise<{ success: boolean }> =>
    api.delete<{ success: boolean }>(`${base(clientId)}/files/${fileId}`);
}
